import type { ButtonPriority } from '@timelyapp/react';
import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import Surface from 'components/Surface';
import config from 'config';
import type { MotionProps } from 'framer-motion';
import { motion } from 'framer-motion';
import React from 'react';
import stepStyles from './Step.module.scss';

export const illustrationAnimationProps: MotionProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: config.animation,
};

export interface StepBaseProps {
  onNext: () => void;
  onBack?: () => void;
  onSkip?: () => void;
  breadcrumbs?: React.ReactNode;
  isLastStep?: boolean;
  isFirstStep: boolean;
  preloadNextStep?: () => void;
}

export interface StepProps extends StepBaseProps {
  children: React.ReactNode;
  illustration?: React.ReactNode;
  nextText?: React.ReactNode;
  nextButtonPriority?: ButtonPriority | null;
  nextButtonTooltip?: React.ReactElement | string;
  hasBack?: boolean;
  skipText?: React.ReactNode;
  hasSkip?: boolean;
  spinner?: boolean;
  delayProgression?: boolean;
  disableProgress?: boolean;
  withNavbar?: boolean;
  skipButtonClass?: string;
  skipButtonPriority?: ButtonPriority | null;
  fullWidth?: boolean;
}

class Step extends React.Component<StepProps> {
  static defaultProps = {
    hasSkip: false,
    illustration: null,
    nextText: null,
    skipText: null,
    hasBack: true,
    spinner: false,
    onSkip: undefined,
    disableProgress: false,
  };

  back = () => {
    const { hasBack, onBack } = this.props;

    if (!!hasBack && onBack && typeof onBack === 'function') {
      onBack();
    }
  };

  nextText = () => {
    if (this.props.nextText) {
      return this.props.nextText;
    }

    if (this.props.isLastStep) {
      return <span>Finish</span>;
    }

    return <span>Next →</span>;
  };

  skipText = () => {
    if (this.props.skipText) {
      return this.props.skipText;
    }
    return <span>Skip</span>;
  };

  render() {
    const showNext = this.props.nextText !== false;
    const skipButton = { hidden: { opacity: 1 } };

    if (this.props.fullWidth) {
      // Full-width layout for new onboarding steps
      return (
        <Surface className={stepStyles.container} data-testid="step_container">
          <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
            <div className={classNames(stepStyles.back, stepStyles.backCentered)}>
              {this.props.hasBack && !this.props.isFirstStep && (
                <Button onClick={this.back} className={stepStyles.backButton} priority="low">
                  ← Back
                </Button>
              )}
              <div className={classNames(stepStyles.breadcrumbs, stepStyles.breadcrumbsCentered)}>
                {this.props.breadcrumbs}
              </div>
            </div>
            <div style={{ marginTop: '3.5rem' }}>{this.props.children}</div>
            <motion.div
              variants={skipButton}
              transition={{ duration: 0.3, delay: this.props.delayProgression ? 5 : 0 }}
              animate="hidden"
              style={{
                marginTop: '2rem',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                maxWidth: '300px',
                margin: '2rem auto 0',
              }}
            >
              {showNext && (
                <Button
                  data-testid="next_button_stepjs"
                  className={classNames(stepStyles.nextbutton)}
                  variant="success"
                  priority={this.props.nextButtonPriority || 'high'}
                  onClick={this.props.onNext}
                  disabled={this.props.disableProgress}
                  onMouseOver={this.props.preloadNextStep}
                  tooltip={this.props.disableProgress ? this.props.nextButtonTooltip : undefined}
                >
                  {this.nextText()}
                </Button>
              )}
              {!!this.props.hasSkip && (
                <Button
                  data-testid="skip_button_stepjs"
                  className={classNames(stepStyles.skipbutton, this.props.skipButtonClass)}
                  onClick={this.props.onSkip}
                  priority={this.props.skipButtonPriority ?? 'low'}
                  onMouseOver={this.props.preloadNextStep}
                  style={{ marginTop: '1rem' }}
                >
                  {this.skipText()}
                </Button>
              )}
            </motion.div>
          </div>
        </Surface>
      );
    }

    // Original two-column layout
    return (
      <Surface className={stepStyles.container} data-testid="step_container">
        <div className={stepStyles.mainWrapper}>
          <div className={stepStyles.main}>
            <div className={stepStyles.back}>
              {this.props.hasBack && !this.props.isFirstStep && (
                <Button onClick={this.back} className={stepStyles.backButton} priority="low">
                  ← Back
                </Button>
              )}
              <div className={stepStyles.breadcrumbs}>{this.props.breadcrumbs}</div>
            </div>
            <div className={stepStyles.form}>{this.props.children}</div>
            <motion.div
              variants={skipButton}
              transition={{ duration: 0.3, delay: this.props.delayProgression ? 5 : 0 }}
              animate="hidden"
              className={stepStyles.footer}
            >
              {showNext && (
                <Button
                  data-testid="next_button_stepjs"
                  className={classNames(stepStyles.nextbutton)}
                  priority={this.props.nextButtonPriority || 'xhigh'}
                  onClick={this.props.onNext}
                  disabled={this.props.disableProgress}
                  onMouseOver={this.props.preloadNextStep}
                  tooltip={this.props.disableProgress ? this.props.nextButtonTooltip : undefined}
                >
                  {this.nextText()}
                </Button>
              )}
              {!!this.props.hasSkip && (
                <Button
                  data-testid="skip_button_stepjs"
                  className={classNames(stepStyles.skipbutton, this.props.skipButtonClass)}
                  onClick={this.props.onSkip}
                  priority={this.props.skipButtonPriority ?? 'low'}
                  onMouseOver={this.props.preloadNextStep}
                >
                  {this.skipText()}
                </Button>
              )}
            </motion.div>
          </div>
        </div>
        {this.props.illustration && (
          <Surface surface={2} className={stepStyles.illustration}>
            {this.props.illustration}
          </Surface>
        )}
      </Surface>
    );
  }
}

export default Step;
